import { useEffect, useState, useCallback } from 'react';

import '../../css/UserAdmin/Global.css';
import '../../css/UserAdmin/SideNav.css';
import '../../css/UserAdmin/Dashboard.css';

import Footer from './Footer';
import Header from './NewHeader';
import NoticeBoard from './NoticeBoard';
import SideNav from './SideNav';
import AiInsight from './Analytics/AiInsights'; 
import QueueAnalytics from './Analytics/QueueAnalytics';
import AdmissionAnalytics from './Analytics/AdmissionAnalytics';
import RegistrarAnalytics from './Analytics/RegistrarAnalytics';
import AccountingAnalytics from './Analytics/AccountingAnalytics';

const Dashboard = () => {
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  const [userDepartment, setUserDepartment] = useState('');
  
  // State for access control
  const [hasSystemAnalyticsAccess, setHasSystemAnalyticsAccess] = useState(false);
  const [allowedDepartmentsForAnalytics, setAllowedDepartmentsForAnalytics] = useState([]);
  const [hasQueueAnalyticsAccess, setHasQueueAnalyticsAccess] = useState(false);
  const [hasAdmissionAnalyticsAccess, setHasAdmissionAnalyticsAccess] = useState(false);
  const [hasRegistrarAnalyticsAccess, setHasRegistrarAnalyticsAccess] = useState(false);
  const [hasAccountingAnalyticsAccess, setHasAccountingAnalyticsAccess] = useState(false);

  const formattedDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const toggleSideNav = useCallback(() => {
    setIsSideNavOpen(prev => !prev);
  }, []);

  // Effect for handling screen resize for the side navigation
  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effect for setting user permissions based on their role
  useEffect(() => {
    const storedUserRole = localStorage.getItem('role') || '';


    // Handle the Super Admin. This role gets universal access.
    if (storedUserRole === 'Super Admin') {
      
      setUserDepartment('Super Admin'); 
      
      // Grant access to ALL analytics sections
      setHasSystemAnalyticsAccess(true);
      setHasQueueAnalyticsAccess(true);
      setHasAdmissionAnalyticsAccess(true);
      setHasRegistrarAnalyticsAccess(true);
      setHasAccountingAnalyticsAccess(true);

      // A Super Admin can view analytics data from ALL departments
      setAllowedDepartmentsForAnalytics(['Admissions', 'Registrar', 'Accounting']);

    // Handle the Sub-Admin. 
    } else if (storedUserRole === 'Sub-Admin') {
      
      setUserDepartment('Sub-Admin');

      setHasSystemAnalyticsAccess(true); // System access
      setHasQueueAnalyticsAccess(true);  // Queue access
      setHasAdmissionAnalyticsAccess(true); // Department-specific access
      setHasRegistrarAnalyticsAccess(true);
      setHasAccountingAnalyticsAccess(true);
      
      // A Sub-Admin can also view analytics data from all departments
      setAllowedDepartmentsForAnalytics(['Admissions', 'Registrar', 'Accounting']);

    // Handle all other standard, department-based roles.
    } else {
      const department = storedUserRole.replace(/\s*\([^)]*\)\s*/g, '').trim();
      setUserDepartment(department);

      // System Analytics is now only for special roles, so it's false for standard departments.
      setHasSystemAnalyticsAccess(false);

      // --- Logic for Queue Analytics Access ---
      const rolesForQueue = ['Registrar', 'Accounting']; 
      const canViewQueue = rolesForQueue.includes(department);
      setHasQueueAnalyticsAccess(canViewQueue);
      
      if (canViewQueue) {
        const departmentAnalyticsScope = {
          'Registrar': ['Registrar'],
          'Accounting': ['Accounting'],
        };
        setAllowedDepartmentsForAnalytics(departmentAnalyticsScope[department] || []);
      } else {
        setAllowedDepartmentsForAnalytics([]);
      }

      // --- Logic for other analytics sections ---
      // A user can only see their own department's analytics.
      setHasAdmissionAnalyticsAccess(department === 'Admissions');
      setHasRegistrarAnalyticsAccess(department === 'Registrar');
      setHasAccountingAnalyticsAccess(department === 'Accounting');
    }
  }, []);

  return (
    <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
      <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
      <div className="admin-content-wrapper">
        <div className="admin-sidenav-container">
          <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
        </div>
        <main className="admin-main-content-area" style={{ marginTop: '1%' }}>
          <div className='content-announcement'>
            <div className='announcement-left'>
              <p className='heading'>Dashboard</p>
              <p className='date'>{formattedDate}</p>
            </div>
            <div className='announcement-right'>
              <NoticeBoard />
            </div>
          </div>

          {/* --- Dedicated System & Security Section for authorized roles --- */}
          {hasSystemAnalyticsAccess && (
            <div className="analytics-section-card" style={{ marginBottom: '2%', marginTop: '3%'}}>
              <AiInsight 
                endpoint="/api/admin/system-logs/insights"
                title="System & Security Overview"
              />
            </div>
          )}

          <QueueAnalytics
            department={userDepartment}
            allowedDepartments={allowedDepartmentsForAnalytics}
            hasAnalyticsAccess={hasQueueAnalyticsAccess}
          />
          <AdmissionAnalytics
            hasAnalyticsAccess={hasAdmissionAnalyticsAccess}
          />
          <RegistrarAnalytics
            hasAnalyticsAccess={hasRegistrarAnalyticsAccess}
          />
          <AccountingAnalytics
            hasAnalyticsAccess={hasAccountingAnalyticsAccess}
          />
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard;